import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { XCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const PayPalCancel = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [bookingData, setBookingData] = useState<any>(null);

  const bookingNumber = searchParams.get('booking_number');

  useEffect(() => {
    if (bookingNumber) {
      handleCancelledPayment();
    } else {
      setLoading(false);
    }
  }, [bookingNumber]);

  const handleCancelledPayment = async () => {
    try {
      console.log('Handling cancelled PayPal payment for booking:', bookingNumber);

      // Update booking status to cancelled
      const { error: updateError } = await supabase
        .from('transfer_bookings')
        .update({
          payment_status: 'cancelled',
          booking_status: 'cancelled'
        })
        .eq('booking_number', bookingNumber);

      if (updateError) {
        console.error('Error updating booking:', updateError);
        toast({
          title: "Error",
          description: "There was an error updating your booking status.",
          variant: "destructive",
        });
      }

      // Get booking data for display
      const { data: booking, error: fetchError } = await supabase
        .from('transfer_bookings')
        .select('*')
        .eq('booking_number', bookingNumber)
        .single();

      if (fetchError) {
        console.error('Error fetching booking:', fetchError);
      } else {
        setBookingData(booking);
      }

      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. You can try again or contact us for assistance.",
        variant: "destructive",
      });

    } catch (err: any) {
      console.error('Error handling cancelled payment:', err);
      toast({
        title: "Error",
        description: "There was an error processing the cancellation.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-8 text-center">
                <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
                <h2 className="text-2xl font-bold mb-2">Processing Cancellation</h2>
                <p className="text-muted-foreground">
                  Please wait while we process your cancellation...
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 p-3 bg-destructive/10 rounded-full w-fit">
                <XCircle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle className="text-2xl text-destructive">Payment Cancelled</CardTitle>
              <p className="text-muted-foreground">
                Your PayPal payment was cancelled and your booking was not completed
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {bookingData && (
                <div className="text-center">
                  <Badge variant="destructive" className="text-lg px-4 py-2">
                    Booking #{bookingData.booking_number} - Cancelled
                  </Badge>
                </div>
              )}

              <div className="bg-muted/50 p-4 rounded-lg">
                <h4 className="font-medium mb-2">What happened?</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• You cancelled the PayPal payment process</li>
                  <li>• Your booking reservation has been cancelled</li>
                  <li>• No charges were made to your account</li>
                  <li>• You can start a new booking at any time</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-medium mb-2 text-blue-800">Need Help?</h4>
                <p className="text-sm text-blue-700 mb-3">
                  If you encountered any issues during the payment process, our support team is here to help.
                </p>
                <div className="flex gap-2">
                  <Button onClick={() => navigate('/contact')} variant="outline" size="sm">
                    Contact Support
                  </Button>
                </div>
              </div>

              <div className="flex gap-4">
                <Button onClick={() => navigate('/transfers')} className="flex-1">
                  Book New Transfer
                </Button>
                <Button onClick={() => navigate('/')} variant="outline" className="flex-1">
                  Return Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default PayPalCancel;